import type {
    AccountByIdApiResponseFull,
    ApiGroup,
    ApiRole,
    ApiTag,
    ApiUser,
    RoleApiResponse,
    UserGroupsApiResponse,
    UsersApiResponse,
} from './apiSchema';
import { type AllowedAction, isAllowedAction, type User, type UserGroup, type UserGroupRole } from '../app/appTypes';
import type { Tag } from './types';
import { accountType, type AccountType } from '../../configuration/login/loginSlice';

export const mapApiUserToUser = (apiUser: ApiUser): User => {
    return {
        id: apiUser.id,
        account_id: apiUser.account_id,
        first_name: apiUser.first_name,
        last_name: apiUser.last_name,
        email: apiUser.email ? apiUser.email : null,
        phone_number: apiUser.phone_number ? apiUser.phone_number : null,
        group_ids: apiUser.group_ids,
        preferred_login: apiUser.preferred_login === 'phone' ? 'phone' : 'email',
        preferred_language: apiUser.preferred_language,
        metadata: apiUser.metadata
            ? {
                  created: apiUser.metadata.created as string, // TODO is this cast reasonable?
                  tenant: apiUser.metadata.tenant === 'prod' ? 'prod' : 'prod', // TODO skip the ternary?
              }
            : undefined,
    };
};

export const mapApiGroupToUserGroup = (apiGroup: ApiGroup): UserGroup => {
    return {
        id: apiGroup.id,
        name: apiGroup.name,
        description: apiGroup.description ? apiGroup.description : '',
        assigned_roles: apiGroup.assigned_roles,
        metadata: {
            allowed_actions: apiGroup.metadata.allowed_actions,
            created: apiGroup.metadata.created as string,
            is_managed_group: apiGroup.metadata.is_managed_group ?? false,
            tenant: apiGroup.metadata.tenant === 'prod' ? 'prod' : 'prod', // TODO skip this ternary?
        },
    };
};

export const mapApiRoleToUserGroupRole = (apiRole: ApiRole): UserGroupRole => ({
    id: apiRole.id,
    key: `intl-msg:roles.names.${apiRole.name}`,
    name: apiRole.name,
    scope_type: apiRole.scope_type === 'account' ? 'account' : 'tag', // TODO this is a bit hacky
});

export const mapToLoadMore = (data: UsersApiResponse | UserGroupsApiResponse): string | undefined =>
    data._links.next?.href;

export const mapToUsers = (data: UsersApiResponse): User[] => data.items.map(mapApiUserToUser);

export const mapToGroups = (data: UserGroupsApiResponse): UserGroup[] => data.items.map(mapApiGroupToUserGroup);

export const mapToUserGroupRole = (data: RoleApiResponse): UserGroupRole[] => data.items.map(mapApiRoleToUserGroupRole);

export const mapApiTagToTag = (apiTag: ApiTag): Tag => ({
    id: apiTag.id,
    type: 'user', // TODO is this really the only possible response
    name: apiTag.name,
    accountId: apiTag.account_id,
});

export const mapToAllowedActions = (rawAllowedActions: string[] | null | undefined): AllowedAction[] | null => {
    if (rawAllowedActions === null || rawAllowedActions === undefined) {
        return null;
    } else {
        return rawAllowedActions.filter(isAllowedAction);
    }
};

const mapStringToAccountType = (rawAccountType: string | undefined): AccountType => {
    switch (rawAccountType) {
        case 'BUYER':
            return accountType.BUYER;
        case 'SUPPLIER':
            return accountType.SUPPLIER;
        // case 'CARRIER':
        default:
            return accountType.CARRIER;
    }
};

export const processAccountsApiResponse = (accountsApiResponse: AccountByIdApiResponseFull) => ({
    id: accountsApiResponse.id,
    name: accountsApiResponse.name,
    accountType: mapStringToAccountType(accountsApiResponse.account_type),
});
